// Generated with util/create-component.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, InputNumber, Button, Checkbox, Row, Form, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';

import { ILoginFormProps, ILoginParams } from './LoginForm.types';
import styles from './LoginForm.module.scss';

const LoginForm: React.FC<ILoginFormProps> = ({ otpSent = false, onLogin, onPasswordReset }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [didResetPassword, setDidResetPassword] = useState(false);

  const submitButtonText = !isForgotMode
    ? t('login.sign_in')
    : !didResetPassword
    ? t('login.reset_password')
    : 'Email Sent';
  const forgotButtonText = isForgotMode ? 'I have a password' : t('login.forgot_password');

  return (
    <Form
      data-testid="LoginForm"
      name="login"
      className={styles.root}
      onFinish={handleSubmit}
      form={form}
      initialValues={{ email: '', password: '', otp: '', remember_me: false }}
    >
      <Form.Item hidden={otpSent} name="email" rules={[{ required: true, message: 'Please input email' }]}>
        <Input
          autoFocus
          aria-label="email"
          type="email"
          size="large"
          prefix={<UserOutlined className={styles.input_icon} />}
          placeholder={t('general.other.email')}
        />
      </Form.Item>
      {!isForgotMode && (
        <Form.Item name="password" hidden={otpSent} rules={[{ required: true, message: 'Please input password' }]}>
          <Input.Password
            aria-label="password"
            size="large"
            prefix={<LockOutlined className={styles.input_icon} />}
            placeholder={t('login.password_label')}
          />
        </Form.Item>
      )}
      <Form.Item hidden={otpSent} name="remember_me" valuePropName="checked">
        <Checkbox>{t('login.remember_me')}</Checkbox>
      </Form.Item>

      {otpSent && (
        <div className={styles.confirmation_code_description}>
          <div>{t('general.otp_confirmation_sent_to_email')}</div>
          <div>{t('general.please_enter')}</div>
        </div>
      )}
      {otpSent && (
        <Form.Item name="otp" rules={[{ required: true, message: t('general.otp.enter_value') }]}>
          <InputNumber
            autoFocus
            aria-label="otp"
            size="large"
            type="number"
            placeholder={t('general.confirmation_code')}
            className={styles.otp_code_input}
          />
        </Form.Item>
      )}
      <Row justify="center">
        <Button
          className={styles.login_button}
          type="primary"
          htmlType="submit"
          size="large"
          shape="round"
          loading={isSubmitting}
          disabled={isForgotMode && didResetPassword}
        >
          {submitButtonText}
        </Button>
      </Row>
      <Row justify="center">
        <Button type="link" disabled={isSubmitting} onClick={() => toggleForgotMode()}>
          {forgotButtonText}
        </Button>
      </Row>
    </Form>
  );

  async function handleSubmit(loginParams: ILoginParams) {
    const { email, password, otp, remember_me } = loginParams;
    const params: ILoginParams = { email, password, remember_me };
    if (otp) {
      params.otp = otp;
    }

    setSubmitting(true);
    try {
      !isForgotMode ? await onLogin(params) : await onPasswordReset(email);
      if (isForgotMode) {
        message.success('Password reset email sent. Please check your inbox');
        setDidResetPassword(true);
      }
    } catch (error) {
      const errorMessage = isForgotMode
        ? 'An error occured, unable to reset password'
        : 'Invalid user credentials or password is expired';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  }

  function toggleForgotMode() {
    form.resetFields(['password']);
    setIsForgotMode(forgot => !forgot);
  }
};

export default LoginForm;
